import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import { infrastructureToGeoJSON, editorPathSegments } from '../../utils/structures'
import { infraWidthExpression, infraStyleFor, dashArrayFor } from '../../utils/overlayStyles'
import { troncColorExpression, troncFocusType, focusedTroncons, TRONC_DIM_COLOR } from '../../utils/troncons'
import { useMapStore, mapState } from '../../store/mapStore'

// Infrastructure: (1) the existing built-infrastructure overlay — per-type
// styled polylines with hover popups, and, in the tool's 'select' mode, per-id
// colours + a click-to-select gesture; and (2) the in-progress path being drawn
// — line + node markers with drag-to-move, right-click-delete and a left-click
// node menu (connect / fork / delete).
//
// `infraMode` is what separates the two mouse behaviours: 'draw' lets the left
// button draw (and shows a crosshair), 'select' leaves every mouse gesture to
// the map so panning and zooming work normally.
export function useInfrastructure(map, {
  showInfrastructure, infrastructureMode, infraMode, constructionMode, infrastructureData,
  infrastructurePath, onInfraPointMove, onInfraPointDelete,
  onInfraNodeMenu, onInfraPointConnect,
  selectedTroncIds, onTroncSelect, styles, regionsData,
}) {
  // The map handlers below are registered once, on the run that creates the
  // layers. Anything they call must therefore come from a ref, or they would
  // keep calling the very first render's closure forever.
  const cb = useRef({})
  cb.current = { onInfraNodeMenu, onInfraPointConnect, onTroncSelect }

  // Cursor the map returns to when nothing more specific applies.
  const idleCursor = () => (mapState().infraMode === 'draw' ? 'crosshair' : '')

  // (1) Existing infrastructure overlay. Forced hidden while the structure tool
  // is active (mirror of the structures overlay being hidden in the infra tool).
  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill')) return

    // The infra tool keeps the overlay on even with the layer toggle off — the
    // tool is worthless if the routes it edits are not drawn.
    const visible = (!!showInfrastructure || !!infrastructureMode) && !constructionMode
    const fc = infrastructureToGeoJSON(visible ? infrastructureData : [])
    const selecting = !!infrastructureMode && infraMode === 'select'

    // Per-type styling (same look as the world line overlays): colour via a
    // match on the feature's infra type, width as the shared zoom ramp scaled by
    // a per-type multiplier. Types absent from the user's overrides fall back to
    // their Monde réel-like default (infraStyleFor). Dash patterns can't be
    // data-driven in MapLibre, so the overlay is three sibling layers
    // (solid / dash / dot) filtered by which types use each pattern.
    const infraTypesPresent = [...new Set((infrastructureData || []).map((i) => i.type).filter(Boolean))]
    const fallbackColor = styles.infrastructure._default?.color || '#FF8C00'
    const typeColorExpr = infraTypesPresent.length
      ? ['match', ['get', 'type'], ...infraTypesPresent.flatMap((t) => [t, infraStyleFor(styles, t).color]), fallbackColor]
      : fallbackColor
    // In selection mode identity beats type: one colour per tronçon. As soon as
    // one is selected the mode narrows to its type — same-type tronçons keep a
    // colour, the rest go grey and translucent, so the candidates for a merge
    // are the only thing standing out.
    const focusType = selecting ? troncFocusType(selectedTroncIds, infrastructureData) : null
    const focusList = selecting ? focusedTroncons(infrastructureData, focusType) : []
    const focusIds = focusList.map((i) => i.id)
    const infraColorExpr = selecting
      ? troncColorExpression(focusList, focusType ? TRONC_DIM_COLOR : fallbackColor)
      : typeColorExpr
    const infraOpacityExpr = focusType
      ? ['case', ['in', ['get', 'id'], ['literal', focusIds]], 0.95, 0.28]
      : 0.85
    const infraWidthExpr = infraWidthExpression(infraTypesPresent.length
      ? ['match', ['get', 'type'], ...infraTypesPresent.flatMap((t) => [t, infraStyleFor(styles, t).width]), 1]
      : 1)

    const typesByDash = { solid: [], dash: [], dot: [] }
    infraTypesPresent.forEach((t) => {
      const d = infraStyleFor(styles, t).dash || 'solid'
      ;(typesByDash[d] || typesByDash.solid).push(t)
    })
    const INFRA_LAYERS = [
      { id: 'infrastructure-lines', dash: 'solid', cap: 'round' },
      { id: 'infrastructure-lines-dash', dash: 'dash', cap: 'butt' },
      { id: 'infrastructure-lines-dot', dash: 'dot', cap: 'round' },
    ]
    const filterFor = (dash) => {
      if (dash === 'solid') {
        // Solid catches everything not explicitly dash/dot (incl. unknown types)
        const others = [...typesByDash.dash, ...typesByDash.dot]
        return others.length ? ['!', ['in', ['get', 'type'], ['literal', others]]] : null
      }
      const mine = typesByDash[dash]
      return ['in', ['get', 'type'], ['literal', mine]]
    }

    const src = map.current.getSource('infrastructure-lines')
    if (!src) {
      map.current.addSource('infrastructure-lines', { type: 'geojson', data: fc })
      // Selection halo, added first so it paints *under* the routes: a white
      // casing around every tronçon currently in the multi-selection.
      map.current.addLayer(
        {
          id: 'infrastructure-tronc-selected',
          type: 'line',
          source: 'infrastructure-lines',
          layout: { 'line-cap': 'round', 'line-join': 'round', visibility: 'none' },
          filter: ['in', ['get', 'id'], ['literal', []]],
          paint: {
            'line-color': '#FFFFFF',
            'line-width': infraWidthExpression(3.2),
            'line-opacity': 0.9,
          },
        },
        map.current.getLayer('structures-points') ? 'structures-points' : undefined
      )
      INFRA_LAYERS.forEach(({ id, dash, cap }) => {
        map.current.addLayer(
          {
            id,
            type: 'line',
            source: 'infrastructure-lines',
            layout: { 'line-cap': cap, 'line-join': 'round' },
            paint: {
              'line-color': infraColorExpr,
              'line-width': infraWidthExpr,
              'line-opacity': 0.85,
              ...(dash !== 'solid' ? { 'line-dasharray': dashArrayFor(dash) } : {}),
            },
          },
          map.current.getLayer('structures-points') ? 'structures-points' : undefined
        )
      })

      const infraPopup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'region-hover-popup infra-hover-popup',
      })

      INFRA_LAYERS.forEach(({ id }) => {
        map.current.on('mousemove', id, (e) => {
          if (mapState().structDragActive) return
          if (!e.features || e.features.length === 0) return
          const p = e.features[0].properties
          infraPopup.setLngLat(e.lngLat).setHTML(`
            <div style="font-size: 12px; padding: 4px;">
              <strong>#${p.id}</strong> — ${p.type}<br/>
              ${Number(p.length_km).toLocaleString('fr-FR')} km · ${p.region_name || `Région #${p.region_id}`}
            </div>
          `).addTo(map.current)
        })
        map.current.on('mouseleave', id, () => infraPopup.remove())

        // Select gesture: in 'select' mode a click on one of your own routes
        // adds it to (or removes it from) the selection — every action on it
        // then lives in the sidebar, where it can be read before it is clicked.
        // The overlay only ever contains the caller's own infrastructure
        // (SSO-scoped server-side), so any route here is selectable.
        map.current.on('click', id, (e) => {
          if (!e.features || e.features.length === 0) return
          if (!mapState().infrastructureModeActive) return
          if (mapState().infraMode !== 'select') return
          // A node drag that ended over a route still fires this click; the
          // node handler flags it so the release does not also flip a selection.
          const suppress = mapState().suppressNextMapClick
          if (suppress && Date.now() - suppress < 400) return
          cb.current.onTroncSelect?.(e.features[0].properties.id)
        })

        // Hovering a selectable route says so, rather than leaving the pan
        // cursor and hoping the popup is noticed.
        map.current.on('mouseenter', id, () => {
          if (mapState().structDragActive) return
          if (!mapState().infrastructureModeActive) return
          if (mapState().infraMode !== 'select') return
          map.current.getCanvas().style.cursor = 'pointer'
        })
        map.current.on('mouseleave', id, () => {
          if (mapState().structDragActive) return
          if (!mapState().infrastructureModeActive) return
          map.current.getCanvas().style.cursor = idleCursor()
        })
      })
    } else {
      src.setData(fc)
    }

    INFRA_LAYERS.forEach(({ id, dash }) => {
      if (!map.current.getLayer(id)) return
      map.current.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
      // Re-apply per-type colour/width/filters every run so live style edits and
      // newly seen types both take effect on the existing layers.
      map.current.setPaintProperty(id, 'line-color', infraColorExpr)
      map.current.setPaintProperty(id, 'line-width', infraWidthExpr)
      map.current.setPaintProperty(id, 'line-opacity', infraOpacityExpr)
      map.current.setFilter(id, filterFor(dash))
    })

    // Selection halo: only ever visible inside the selection mode.
    if (map.current.getLayer('infrastructure-tronc-selected')) {
      const ids = selecting ? (selectedTroncIds || []) : []
      map.current.setLayoutProperty(
        'infrastructure-tronc-selected', 'visibility', ids.length ? 'visible' : 'none'
      )
      map.current.setFilter('infrastructure-tronc-selected', ['in', ['get', 'id'], ['literal', ids]])
    }
  }, [map, infrastructureData, showInfrastructure, infrastructureMode, infraMode, constructionMode, regionsData, styles.infrastructure, selectedTroncIds])

  // (2) In-progress path drawing: line overlay + node markers. The path may hold
  // several segments (branch networks) — one LineString per segment.
  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill')) return

    const hasPath = infrastructureMode && infrastructurePath && infrastructurePath.length > 0
    const segments = hasPath ? editorPathSegments(infrastructurePath) : []
    const lineFc = {
      type: 'FeatureCollection',
      features: segments
        .filter((coords) => coords.length >= 2)
        .map((coords) => ({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords },
          properties: {},
        })),
    }
    const pointFc = {
      type: 'FeatureCollection',
      features: hasPath
        ? infrastructurePath.map((p, index) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [p.lngLat.lng, p.lngLat.lat] },
            properties: { region_id: p.region.region_id, name: p.region.name, index },
          }))
        : [],
    }

    // Line layer
    const lineSrc = map.current.getSource('infra-path-line')
    if (!lineSrc) {
      map.current.addSource('infra-path-line', { type: 'geojson', data: lineFc })
      map.current.addLayer({
        id: 'infra-path-line',
        type: 'line',
        source: 'infra-path-line',
        paint: {
          'line-color': '#FFD700',
          'line-width': 4,
          'line-dasharray': [2, 1],
        },
      })
    } else {
      lineSrc.setData(lineFc)
    }
    if (map.current.getLayer('infra-path-line')) {
      map.current.setLayoutProperty('infra-path-line', 'visibility', hasPath ? 'visible' : 'none')
    }

    // Point markers + an invisible twin with a much larger radius that does the
    // actual hit-testing — grabbing a node no longer requires pixel-perfect aim.
    const pointSrc = map.current.getSource('infra-path-points')
    if (!pointSrc) {
      map.current.addSource('infra-path-points', { type: 'geojson', data: pointFc })
      map.current.addLayer({
        id: 'infra-path-points',
        type: 'circle',
        source: 'infra-path-points',
        paint: {
          'circle-radius': 7,
          'circle-color': '#FFD700',
          'circle-stroke-color': '#1a1a1a',
          'circle-stroke-width': 2,
        },
      })
      map.current.addLayer({
        id: 'infra-path-points-hit',
        type: 'circle',
        source: 'infra-path-points',
        paint: {
          'circle-radius': 16,
          'circle-opacity': 0,
          'circle-stroke-width': 0,
        },
      })

      // Path corrections, all on the enlarged hit layer: drag a node to move
      // it, right-click to delete it, plain click (press+release without
      // moving) opens the node menu — connect to another node, fork a branch,
      // delete, or switch to tronçon selection. While a connection is pending
      // (`infraConnectFrom`), that same plain click is the second half of the
      // gesture and links the two nodes instead of opening a menu.
      // Handlers registered once; parent callbacks only touch state via
      // functional updates so the captured instances stay valid.
      map.current.on('mousedown', 'infra-path-points-hit', (e) => {
        if (!mapState().infrastructureModeActive || !onInfraPointMove) return
        if (e.originalEvent.button !== 0) return
        if (!e.features || e.features.length === 0) return
        const index = e.features[0].properties.index
        e.preventDefault()
        useMapStore.setState({ structDragActive: true }) // reuse the drag lock: blocks free-draw + hover popups
        map.current.getCanvas().style.cursor = 'grabbing'

        const start = e.point
        let moved = false
        const onMove = (ev) => {
          if (!moved) {
            const dx = ev.point.x - start.x
            const dy = ev.point.y - start.y
            if (dx * dx + dy * dy < 16) return // 4px slop before it counts as a drag
            moved = true
          }
          onInfraPointMove(index, ev.lngLat)
        }
        const onUp = () => {
          map.current.off('mousemove', onMove)
          useMapStore.setState({ structDragActive: false })
          useMapStore.setState({ suppressNextMapClick: Date.now() })
          map.current.getCanvas().style.cursor = idleCursor()
          if (moved) return
          const connectFrom = mapState().infraConnectFrom
          if (connectFrom != null && connectFrom !== index) {
            cb.current.onInfraPointConnect?.(connectFrom, index)
            return
          }
          cb.current.onInfraNodeMenu?.({ index, x: start.x, y: start.y })
        }
        map.current.on('mousemove', onMove)
        map.current.once('mouseup', onUp)
      })

      map.current.on('contextmenu', 'infra-path-points-hit', (e) => {
        if (!mapState().infrastructureModeActive || !onInfraPointDelete) return
        if (!e.features || e.features.length === 0) return
        e.preventDefault()
        onInfraPointDelete(e.features[0].properties.index)
      })

      map.current.on('mousemove', 'infra-path-points-hit', () => {
        if (mapState().structDragActive) return
        if (!mapState().infrastructureModeActive) return
        map.current.getCanvas().style.cursor = 'grab'
      })
      map.current.on('mouseleave', 'infra-path-points-hit', () => {
        if (mapState().structDragActive) return
        if (!mapState().infrastructureModeActive) return
        map.current.getCanvas().style.cursor = idleCursor()
      })
    } else {
      pointSrc.setData(pointFc)
    }
    const pointVis = hasPath ? 'visible' : 'none'
    if (map.current.getLayer('infra-path-points')) {
      map.current.setLayoutProperty('infra-path-points', 'visibility', pointVis)
    }
    if (map.current.getLayer('infra-path-points-hit')) {
      map.current.setLayoutProperty('infra-path-points-hit', 'visibility', pointVis)
    }
  }, [map, infrastructureMode, infrastructurePath, regionsData])
}
